/**
 * Room slug <-> enum helpers for the Home Safety Audit wizard.
 * Shared by AuditEntryPage, RoomPage, and ResultsPage.
 */

import type { RoomArea, SessionResponseDto } from '../types/homeAudit.types';

export const ROOM_ORDER = [
  'bedroom',
  'bathroom',
  'kitchen',
  'living-areas',
  'entry-exterior',
  'stairs',
] as const;

export type RoomSlug = (typeof ROOM_ORDER)[number];

export const ROOM_SLUG_TO_ENUM: Record<RoomSlug, RoomArea> = {
  'bedroom': 'BEDROOM',
  'bathroom': 'BATHROOM',
  'kitchen': 'KITCHEN',
  'living-areas': 'LIVING_AREAS',
  'entry-exterior': 'ENTRY_EXTERIOR',
  'stairs': 'STAIRS',
};

export const ROOM_ENUM_TO_SLUG: Record<RoomArea, RoomSlug> = {
  BEDROOM: 'bedroom',
  BATHROOM: 'bathroom',
  KITCHEN: 'kitchen',
  LIVING_AREAS: 'living-areas',
  ENTRY_EXTERIOR: 'entry-exterior',
  STAIRS: 'stairs',
};

export const ROOM_LABELS: Record<RoomArea, string> = {
  BEDROOM: 'Bedroom',
  BATHROOM: 'Bathroom',
  KITCHEN: 'Kitchen',
  LIVING_AREAS: 'Living Areas',
  ENTRY_EXTERIOR: 'Entry & Exterior',
  STAIRS: 'Stairs',
};

export const ROOM_INTROS: Record<RoomArea, string> = {
  BEDROOM: 'Most falls happen at night on the path between the bed and bathroom. Good lighting and a clear path are the highest-impact changes you can make.',
  BATHROOM: 'The bathroom is the most dangerous room in the home for older adults — wet surfaces and confined spaces make grab bars and non-slip mats essential.',
  KITCHEN: 'Reaching for high shelves and slippery floors near the sink are the leading kitchen hazards. Having everyday items within easy reach eliminates most of the risk.',
  LIVING_AREAS: 'Loose rugs, low lighting, and cords across walkways are the top trip hazards in living areas and hallways. Clearing these is quick and free.',
  ENTRY_EXTERIOR: 'Uneven walkways, poor lighting, and missing handrails at steps are the main outdoor fall risks. These also matter for emergency access by first responders.',
  STAIRS: 'Stairs account for a disproportionate share of serious fall injuries. A secure handrail, non-slip treads, and lighting at both ends cover the biggest risks.',
};

export const isRoomSlug = (value: string | undefined): value is RoomSlug =>
  !!value && (ROOM_ORDER as readonly string[]).includes(value);

export const roomAreaFromSlug = (slug: string | undefined): RoomArea => {
  if (!isRoomSlug(slug)) {
    throw new Error(`Unknown room slug: ${slug}`);
  }
  return ROOM_SLUG_TO_ENUM[slug];
};

export const activeRoomOrder = (stairsNa: boolean): readonly RoomSlug[] =>
  stairsNa ? ROOM_ORDER.filter((s) => s !== 'stairs') : ROOM_ORDER;

export const nextRoomSlug = (
  currentSlug: RoomSlug,
  stairsNa: boolean,
): RoomSlug | null => {
  const order = activeRoomOrder(stairsNa);
  const idx = order.indexOf(currentSlug);
  if (idx < 0 || idx === order.length - 1) return null;
  return order[idx + 1];
};

export const prevRoomSlug = (
  currentSlug: RoomSlug,
  stairsNa: boolean,
): RoomSlug | null => {
  const order = activeRoomOrder(stairsNa);
  const idx = order.indexOf(currentSlug);
  if (idx <= 0) return null;
  return order[idx - 1];
};

export const firstIncompleteRoomSlug = (
  session: SessionResponseDto,
): RoomSlug | null => {
  const order = activeRoomOrder(session.stairsNa);
  const answeredRooms = new Set(session.responses.map((r) => r.roomArea));
  for (const slug of order) {
    if (!answeredRooms.has(ROOM_SLUG_TO_ENUM[slug])) return slug;
  }
  return null;
};
